import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { API_URL } from '../utils/Functions';

const BlogDetail = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    fetch(`${API_URL}/api/public/blogs/${slug}`)
      .then(res => res.json())
      .then(data => {
        if (data.status === 'success' && data.data) {
          setBlog(data.data);
        } else if (data.id) {
          // If API returns the raw object directly
          setBlog(data);
        } else {
          setError('Blog not found.');
        }
      })
      .catch(err => {
        console.error('Failed to load blog:', err);
        setError('Failed to load blog details.');
      })
      .finally(() => setLoading(false));
  }, [slug]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  return (
    <>
      <Navbar />
      
      <style>{`
        .bd-page {
          background: #ffffff;
          min-height: calc(100vh - 60px);
          font-family: 'Inter', sans-serif;
          color: #0f172a;
        }

        .bd-banner {
          width: 100%;
          height: 60vh;
          min-height: 400px;
          max-height: 600px;
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }
        
        .bd-banner-img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          z-index: 0;
        }

        .bd-banner-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(to bottom, rgba(6, 24, 48, 0.4) 0%, rgba(6, 24, 48, 0.95) 100%);
          z-index: 1;
        }

        .bd-banner-content {
          position: relative;
          z-index: 2;
          max-width: 900px;
          text-align: center;
          padding: 0 24px;
          margin-top: 40px;
        }

        .bd-content-container {
          max-width: 1000px;
          margin: 0 auto;
          padding: 80px 24px 100px;
        }

        .bd-content-layout {
          display: flex;
          align-items: flex-start;
          gap: 60px;
        }

        .bd-sidebar {
          position: sticky;
          top: 100px;
          width: 60px;
          display: flex;
          flex-direction: column;
          gap: 16px;
          align-items: center;
        }
        
        .bd-sidebar-title {
          font-family: 'Inter', sans-serif;
          font-size: 11px;
          font-weight: 700;
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 2px;
          writing-mode: vertical-rl;
          transform: rotate(180deg);
          margin-bottom: 20px;
        }

        .bd-sidebar-btn {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: #ffffff;
          border: 1px solid #eef1f5;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #64748b;
          text-decoration: none;
          transition: all 0.2s;
          font-size: 16px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.02);
        }
        
        .bd-sidebar-btn:hover {
          background: #CFA047;
          color: white;
          border-color: #CFA047;
          transform: scale(1.1);
        }

        .bd-article-wrapper {
          flex: 1;
          max-width: 760px;
        }

        .bd-back-light {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          color: #CFA047;
          font-weight: 600;
          font-size: 14px;
          text-decoration: none;
          margin-bottom: 24px;
          text-transform: uppercase;
          letter-spacing: 1px;
          transition: color 0.2s, transform 0.2s;
        }
        
        .bd-back-light:hover {
          color: #ffffff;
          transform: translateX(-4px);
        }

        .bd-title-light {
          font-size: 52px;
          font-weight: 800;
          color: #ffffff;
          line-height: 1.15;
          margin-bottom: 24px;
          letter-spacing: -1px;
          text-shadow: 0 4px 20px rgba(0,0,0,0.3);
        }

        .bd-meta-light {
          display: flex;
          align-items: center;
          justify-content: center;
          flex-wrap: wrap;
          gap: 24px;
          color: rgba(255,255,255,0.85);
          font-size: 15px;
          font-weight: 500;
        }

        .bd-meta-item {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .bd-meta-item i {
          color: #CFA047;
          font-size: 16px;
        }

        /* Typography for reading */
        .bd-article {
          font-family: 'Inter', sans-serif;
          font-size: 19px;
          line-height: 1.85;
          color: #334155;
        }

        /* Drop cap for first paragraph to make it look premium */
        .bd-article > p:first-of-type::first-letter {
          float: left;
          font-size: 72px;
          line-height: 60px;
          font-weight: 900;
          color: #CFA047;
          margin: 12px 16px 0 0;
        }

        .bd-article h1, .bd-article h2, .bd-article h3, .bd-article h4 {
          font-family: 'Inter', sans-serif;
          color: #0f172a;
          margin-top: 56px;
          margin-bottom: 24px;
          font-weight: 800;
          line-height: 1.3;
          letter-spacing: -0.5px;
        }
        
        .bd-article h2 { font-size: 32px; }
        .bd-article h3 { font-size: 24px; }
        
        .bd-article p { margin-bottom: 32px; font-weight: 400; }
        
        /* Premium stylistic separator */
        .bd-article-separator {
          width: 60px;
          height: 4px;
          background: #CFA047;
          border-radius: 2px;
          margin-bottom: 40px;
        }

        .bd-article img {
          max-width: 100%;
          height: auto;
          border-radius: 12px;
          margin: 40px 0;
          box-shadow: 0 4px 20px rgba(0,0,0,0.06);
        }

        .bd-article blockquote {
          margin: 48px 0;
          padding: 32px 40px;
          background: #f8fafc;
          border-left: 4px solid #CFA047;
          border-radius: 0 16px 16px 0;
          font-style: italic;
          font-size: 24px;
          color: #1e293b;
          line-height: 1.5;
        }

        .bd-article ul, .bd-article ol {
          margin-bottom: 32px;
          padding-left: 24px;
        }

        .bd-article li {
          margin-bottom: 12px;
          padding-left: 8px;
        }
        
        .bd-article li::marker {
          color: #CFA047;
          font-weight: 600;
        }

        .bd-author-box {
          margin-top: 80px;
          padding: 40px;
          background: #061830;
          border-radius: 16px;
          color: white;
          display: flex;
          align-items: center;
          gap: 30px;
        }

        .bd-author-avatar {
          width: 72px;
          height: 72px;
          border-radius: 50%;
          background: #CFA047;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 28px;
          color: white;
          flex-shrink: 0;
        }

        .bd-author-info h4 {
          margin: 0 0 8px 0;
          font-size: 20px;
          font-weight: 700;
          color: #ffffff;
        }

        .bd-author-info p {
          margin: 0;
          color: rgba(255,255,255,0.7);
          font-size: 15px;
          line-height: 1.6;
        }

        @media (max-width: 992px) {
          .bd-content-layout { flex-direction: column; gap: 40px; }
          .bd-sidebar { position: static; width: 100%; flex-direction: row; justify-content: flex-start; }
          .bd-sidebar-title { writing-mode: horizontal-tb; transform: none; margin-bottom: 0; margin-right: 16px; }
          .bd-author-box { flex-direction: column; text-align: center; gap: 20px; padding: 30px 20px; }
        }

        @media (max-width: 768px) {
          .bd-title-light { font-size: 36px; margin-bottom: 24px; }
          .bd-banner { height: 40vh; min-height: 350px; }
          .bd-content-container { padding-top: 40px; }
          .bd-article { font-size: 18px; }
          .bd-article blockquote { font-size: 22px; padding-left: 20px; margin: 32px 0; }
        }
      `}</style>

      <div className="bd-page">
        {loading ? (
          <div style={{ textAlign: 'center', padding: '120px 20px' }}>
            <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem', color: '#061830' }}>
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading blog details...</p>
          </div>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: '120px 20px' }}>
            <i className="fa-solid fa-triangle-exclamation" style={{ fontSize: '48px', color: '#ef4444', marginBottom: '20px' }}></i>
            <h2>{error}</h2>
            <Link to="/blogs" className="btn btn-primary mt-4" style={{ background: '#061830', border: 'none', padding: '12px 24px', borderRadius: '8px' }}>Back to Blogs</Link>
          </div>
        ) : blog ? (
          <>
            <>
              <div className="bd-banner">
                <img src={blog.image || blog.img || 'https://images.unsplash.com/photo-1499750310107-5fef28a66643?auto=format&fit=crop&w=1600&q=80'} alt={blog.title} className="bd-banner-img" />
                <div className="bd-banner-overlay"></div>
                
                <div className="bd-banner-content">
                  <Link to="/blogs" className="bd-back-light">
                    <i className="fa-solid fa-arrow-left-long"></i> Back to Insights
                  </Link>
                  
                  <h1 className="bd-title-light">{blog.title}</h1>
                  
                  <div className="bd-meta-light">
                    <div className="bd-meta-item">
                      <i className="fa-regular fa-calendar"></i> {formatDate(blog.created_at)}
                    </div>
                    {blog.views !== undefined && (
                      <div className="bd-meta-item">
                        <i className="fa-regular fa-eye"></i> {Intl.NumberFormat('en-US', { notation: 'compact' }).format(blog.views)} Views
                      </div>
                    )}
                    <div className="bd-meta-item">
                      <i className="fa-solid fa-tag"></i> Article
                    </div>
                  </div>
                </div>
              </div>

              <div className="bd-content-container">
                <div className="bd-content-layout">
                  {/* Floating Sidebar */}
                  <div className="bd-sidebar">
                    <div className="bd-sidebar-title">Share</div>
                    <a href="#!" className="bd-sidebar-btn" title="Share on Twitter"><i className="fa-brands fa-x-twitter"></i></a>
                    <a href="#!" className="bd-sidebar-btn" title="Share on LinkedIn"><i className="fa-brands fa-linkedin-in"></i></a>
                    <a href="#!" className="bd-sidebar-btn" title="Share on Facebook"><i className="fa-brands fa-facebook-f"></i></a>
                    <button className="bd-sidebar-btn" title="Copy Link" onClick={() => { navigator.clipboard.writeText(window.location.href); alert('Link copied to clipboard!'); }}><i className="fa-solid fa-link"></i></button>
                  </div>

                  {/* Main Content Area */}
                  <div className="bd-article-wrapper">
                    <div className="bd-article-separator"></div>
                    <article className="bd-article" dangerouslySetInnerHTML={{ __html: blog.content }}></article>

                    <div className="bd-author-box">
                      <div className="bd-author-avatar">
                        <i className="fa-solid fa-pen-nib"></i>
                      </div>
                      <div className="bd-author-info">
                        <h4>TaxPortal Editorial Team</h4>
                        <p>Providing expert insights, updates, and comprehensive guides on taxation, corporate compliance, and business growth strategies.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          </>
        ) : null}
      </div>

      <Footer />
    </>
  );
};

export default BlogDetail;
